"use client";
import { useState, useEffect, createContext, useContext, useCallback } from "react";

export type ToastType = 'success' | 'error' | 'warning' | 'info';

type ToastData = {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
};

type ToastContextType = {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration: number = 5000) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-8 right-8 z-50 flex flex-col gap-3">
        {toasts.map(toast => (
          <ToastItem
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const EMOJI_MAP: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: '💡',
};

type ToastItemProps = {
  message: string;
  type: ToastType;
  duration: number;
  onClose: () => void;
};

function ToastItem({ message, type, duration, onClose }: ToastItemProps) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
        onClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, onClose]);

  const bgColor = type === 'error' ? 'bg-red-500' : 
                  type === 'success' ? 'bg-emerald-500' : 
                  type === 'warning' ? 'bg-amber-500' : 'bg-neutral-900';

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl overflow-hidden min-w-[280px] animate-in slide-in-from-right">
      <div className="p-5 flex items-start justify-between gap-6">
        <div className="flex flex-col">
          <span className={`text-lg mb-1 ${type === 'success' ? 'text-emerald-500' : type === 'error' ? 'text-red-500' : type === 'warning' ? 'text-amber-500' : 'text-neutral-900'}`}>
            {EMOJI_MAP[type]}
          </span>
          <span className="text-base text-neutral-900">{message}</span>
        </div>
        <button
          onClick={onClose}
          className="text-neutral-300 hover:text-neutral-500 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M1 1l12 12M13 1L1 13" />
          </svg>
        </button>
      </div>
      <div className="h-1 bg-neutral-100">
        <div 
          className={`h-full ${bgColor} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// Legacy Toast component for backward compatibility (Idiot toast)
type LegacyToastProps = {
  message: string;
  duration?: number;
  onClose: () => void;
};

export function LegacyToast({ message, duration = 10000, onClose }: LegacyToastProps) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      
      if (remaining === 0) {
        clearInterval(interval);
        onClose();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [duration, onClose]);

  return (
    <div className="fixed bottom-8 right-8 z-50">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl overflow-hidden min-w-[280px]">
        <div className="p-5 flex items-start justify-between gap-6">
          <div className="flex flex-col">
            <span className="text-lg mb-1">🤦</span>
            <span className="text-base text-neutral-900">{message}</span>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-300 hover:text-neutral-500 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>
        </div>
        <div className="h-1 bg-neutral-100">
          <div 
            className="h-full bg-neutral-900 transition-all duration-100 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

